import React from 'react';
import { FileText, Calculator, PieChart, AlertTriangle } from 'lucide-react';

/**
 * Компонент боковой панели с информацией о налоговых методах амортизации
 */
const TaxMethodsSidebar = ({ formData, setFormData }) => {
  // Налоговые ставки амортизации для групп основных средств согласно НК РФ
  const taxRates = [
    { group: '1', linearRate: '14.3%', nonLinearRate: '14.3%', years: '1-2 года' },
    { group: '2', linearRate: '8.8%', nonLinearRate: '8.8%', years: '2-3 года' },
    { group: '3', linearRate: '5.6%', nonLinearRate: '5.6%', years: '3-5 лет' },
    { group: '4', linearRate: '3.8%', nonLinearRate: '3.8%', years: '5-7 лет' },
    { group: '5', linearRate: '2.7%', nonLinearRate: '2.7%', years: '7-10 лет' },
    { group: '6', linearRate: '1.8%', nonLinearRate: '1.8%', years: '10-15 лет' },
    { group: '7', linearRate: '1.3%', nonLinearRate: '1.3%', years: '15-20 лет' },
    { group: '8', linearRate: '1.0%', nonLinearRate: '1.0%', years: '20-25 лет' },
    { group: '9', linearRate: '0.8%', nonLinearRate: '0.8%', years: '25-30 лет' },
    { group: '10', linearRate: '0.7%', nonLinearRate: '0.7%', years: 'более 30 лет' }
  ];

  // Обработчик изменения флажка для включения налогового учета
  const handleTaxCalculationChange = (e) => {
    setFormData(prev => ({
      ...prev,
      includeTaxCalculation: e.target.checked
    }));
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <FileText className="w-5 h-5 mr-2 text-indigo-500" />
        Налоговый учет амортизации
      </h3>

      <div className="mb-4">
        <label className="inline-flex items-center">
          <input
            type="checkbox"
            checked={formData.includeTaxCalculation}
            onChange={handleTaxCalculationChange}
            className="form-checkbox"
          />
          <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
            Включить расчет для налогового учета
          </span>
        </label>
      </div>

      {formData.includeTaxCalculation && (
        <div className="space-y-3 mb-4">
          <div className="flex gap-2">
            <button
              onClick={() => setFormData(prev => ({ ...prev, taxMethod: 'linear' }))}
              className={`flex-1 px-3 py-1.5 text-sm rounded-md border transition-colors ${
                formData.taxMethod === 'linear'
                  ? 'bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/20 dark:border-blue-700 dark:text-blue-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300'
              }`}
            >
              Линейный метод
            </button>
            <button
              onClick={() => setFormData(prev => ({ ...prev, taxMethod: 'non_linear' }))}
              className={`flex-1 px-3 py-1.5 text-sm rounded-md border transition-colors ${
                formData.taxMethod === 'non_linear'
                  ? 'bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/20 dark:border-blue-700 dark:text-blue-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300'
              }`}
            >
              Нелинейный метод
            </button>
          </div>

          <div className="bg-gray-50 dark:bg-gray-700/30 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2 flex items-center">
              <Calculator className="w-4 h-4 mr-1.5 text-indigo-500" />
              Ежемесячные нормы амортизации для группы {formData.assetGroup}
            </h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 dark:text-gray-400">Линейный:</span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {(100 / (parseInt(formData.usefulLife) * 12)).toFixed(2)}%
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 dark:text-gray-400">Нелинейный:</span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {taxRates.find(r => r.group === formData.assetGroup)?.nonLinearRate || '-'}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 border border-blue-100 dark:border-blue-800">
        <h4 className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-2 flex items-center">
          <PieChart className="w-4 h-4 mr-1.5 text-blue-500" />
          Нормы амортизации по НК РФ
        </h4>
        <div className="overflow-x-auto max-h-[200px] scrollbar-thin">
          <table className="min-w-full divide-y divide-blue-200 dark:divide-blue-800 text-xs">
            <thead className="bg-blue-100/50 dark:bg-blue-900/40">
              <tr>
                <th className="px-2 py-1 text-left text-blue-700 dark:text-blue-300">Группа</th>
                <th className="px-2 py-1 text-left text-blue-700 dark:text-blue-300">Срок службы</th>
                <th className="px-2 py-1 text-right text-blue-700 dark:text-blue-300">Норма (мес.)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-blue-100 dark:divide-blue-800">
              {taxRates.map((rate) => (
                <tr key={rate.group} className={
                  rate.group === formData.assetGroup 
                    ? 'bg-blue-100/80 dark:bg-blue-800/30' 
                    : 'hover:bg-blue-100/30 dark:hover:bg-blue-900/20'
                }>
                  <td className="px-2 py-1 whitespace-nowrap font-medium text-blue-800 dark:text-blue-300">{rate.group}</td>
                  <td className="px-2 py-1 whitespace-nowrap text-blue-700 dark:text-blue-400">{rate.years}</td>
                  <td className="px-2 py-1 whitespace-nowrap text-right text-blue-700 dark:text-blue-400">{rate.nonLinearRate}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-4 bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded-lg border border-yellow-100 dark:border-yellow-800">
        <div className="flex items-start">
          <AlertTriangle className="w-4 h-4 text-yellow-500 mt-1 mr-2" />
          <div>
            <h4 className="text-sm font-medium text-yellow-700 dark:text-yellow-300">
              Ограничения налогового учета
            </h4>
            <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
              В соответствии с Налоговым кодексом РФ:
              <br />- 8-10 группы амортизируются только линейным методом
              <br />- Нелинейный метод применяется всей амортизационной группе
              <br />- При остатке менее 20% стоимости переходят на линейный метод
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaxMethodsSidebar;